import React, { useEffect, useRef } from 'react';
import axios from 'axios';

// Empty context to let child components optionally access the manager
export const VersionContext = React.createContext(null);

export default function VersionManager({ children }) {
  const serverStartedAtRef = useRef(null);
  const checkingRef = useRef(false);
  // Set once a backend restart is detected. We DON'T reload immediately — we wait
  // for the tab to lose focus / go idle so we never yank the page mid-interaction.
  const pendingReloadRef = useRef(false);

  const doReload = React.useCallback(() => {
    if (!pendingReloadRef.current) return;
    window.location.href = window.location.pathname + '?v=' + Date.now();
  }, []);

  // Check version against backend /health endpoint
  const checkVersion = React.useCallback(async () => {
    if (checkingRef.current) return;
    // Already know we need to reload — no point re-checking.
    if (pendingReloadRef.current) return;
    try {
      checkingRef.current = true;
      const res = await axios.get('/health', {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });

      const newStartedAt = res.data.serverStartedAt;

      if (!serverStartedAtRef.current && newStartedAt) {
        // Initial load - store the startup timestamp
        serverStartedAtRef.current = newStartedAt;
      } else if (serverStartedAtRef.current && newStartedAt && serverStartedAtRef.current !== newStartedAt) {
        // Backend restarted. Flag a pending reload but only actually reload when the
        // user isn't actively looking at / interacting with the page. This prevents
        // the jarring "page reloaded while I was clicking" behaviour during deploys.
        console.log(`[VersionManager] Backend restart detected (old ${serverStartedAtRef.current}, new ${newStartedAt}). Reload deferred until tab is idle.`);
        pendingReloadRef.current = true;
        // If the tab is already hidden, reload right away (safe — user isn't looking).
        if (document.visibilityState === 'hidden') {
          setTimeout(doReload, 300);
        }
      }
    } catch (err) {
      console.warn('[VersionManager] Health check failed, server might be restarting...', err.message);
    } finally {
      checkingRef.current = false;
    }
  }, [doReload]);

  // Initial mount check
  useEffect(() => {
    checkVersion();
  }, [checkVersion]);

  // Reload when the tab goes to the background (user switched away) — the safest
  // moment to swap in a new build without interrupting anything.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' && pendingReloadRef.current) {
        doReload();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [doReload]);

  // Expose manual check function so other components (like WebSocket handlers) can trigger a check on reconnect
  return (
    <VersionContext.Provider value={{ checkVersion }}>
      {children}
    </VersionContext.Provider>
  );
}
